import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppSelector } from "../../../../common/hooks/hooks";
import { useToggleCompareItemMutation } from "../api/compareApi";
import { selectIsInCompare } from "../compareSelectors";
import { useMutationWithFeedback } from "../../../../common/hooks/useMutationWithFeedback";

/**
 * A custom hook to encapsulate logic for toggling a product in the compare list.
 * Now uses useMutationWithFeedback for consistent UX.
 */
export const useToggleCompareItem = (productId: number) => {
  const { t } = useTranslation();
  const isInCompare = useAppSelector(selectIsInCompare(productId));
  const [toggleCompareItem, result] = useToggleCompareItemMutation();

  const { trigger, isLoading } = useMutationWithFeedback<number[], number>({
    mutation: [toggleCompareItem, result],
    // We handle success messages manually in the onSuccess callback because
    // the message depends on whether we added or removed the item.
    successMessage: null,
    errorMessages: {
      default: t("feedback.compare.error", "Could not update compare list."),
      compare_limit_reached: t(
        "feedback.compare.limitError",
        "Compare list is full."
      ),
    },
  });

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;

    // Capture state before mutation
    const wasInCompare = isInCompare;

    const response = await trigger(productId);

    if (response.success) {
      const message = wasInCompare
        ? t("feedback.compare.removedSuccess", "Removed from compare list")
        : t("feedback.compare.addedSuccess", "Added to compare list!");

      if (wasInCompare) {
        toast(message, {
          icon: "🔄",
          className: "app-toast app-toast--info",
        });
      } else {
        toast.success(message);
      }
    }
  };

  return {
    handleToggle,
    isInCompare,
    isLoading,
  };
};
